package ai.jessie.web.pages

import ai.jessie.web.theme.BadgeTone
import ai.jessie.web.theme.BrandStyle
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CheckHintStyle
import ai.jessie.web.theme.CheckStyle
import ai.jessie.web.theme.DangerVar
import ai.jessie.web.theme.DayStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldOptionalStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.Grid2Style
import ai.jessie.web.theme.GroundVar
import ai.jessie.web.theme.GroupTableRowVariant
import ai.jessie.web.theme.HeadTableRowVariant
import ai.jessie.web.theme.HeatDayVariants
import ai.jessie.web.theme.HoursVar
import ai.jessie.web.theme.ICONS
import ai.jessie.web.theme.IconStyle
import ai.jessie.web.theme.Ink3Var
import ai.jessie.web.theme.InkVar
import ai.jessie.web.theme.Kpi
import ai.jessie.web.theme.LabelStyle
import ai.jessie.web.theme.LineVar
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.NavItem
import ai.jessie.web.theme.NavVar
import ai.jessie.web.theme.NumInputStyle
import ai.jessie.web.theme.NumStyle
import ai.jessie.web.theme.PrimaryTintVar
import ai.jessie.web.theme.PrimaryVar
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SegmentStyle
import ai.jessie.web.theme.SegmentedStyle
import ai.jessie.web.theme.SideItem
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.SurfaceVar
import ai.jessie.web.theme.TabStyle
import ai.jessie.web.theme.TableNumStyle
import ai.jessie.web.theme.TableRowKind
import ai.jessie.web.theme.TableRowStyle
import ai.jessie.web.theme.TableStyle
import ai.jessie.web.theme.TabsStyle
import ai.jessie.web.theme.TextAreaStyle
import ai.jessie.web.theme.TodayDayVariant
import ai.jessie.web.theme.TotalTableRowVariant
import ai.jessie.web.theme.badge
import ai.jessie.web.theme.card
import ai.jessie.web.theme.columns
import ai.jessie.web.theme.icon
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.number
import ai.jessie.web.theme.page
import ai.jessie.web.theme.pageHeader
import ai.jessie.web.theme.sideNav
import ai.jessie.web.theme.themedButton
import ai.jessie.web.theme.topNav
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.forms.Checkbox
import com.varabyte.kobweb.silk.components.forms.Switch
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.components.forms.UnstyledInputVariant
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

/**
 * Acceptance page for the design tokens and components (docs/design). Renders every token and
 * component once, in the current colour mode; compare against the two style-guide artboards. Static
 * content only – no ports, no session.
 */
@Page
@Composable
fun styleguidePage() {
    topNav(
        items = NAV_ITEMS,
        current = "/styleguide",
        initials = "SL",
        timer = "01:42:13 · Sanitas · Projektmitarbeit",
    )
    page {
        pageHeader(
            title = "Style Guide",
            kicker = "Design v2",
            subtitle = "Tokens und Komponenten für die Kotlin/Kobweb-Neuimplementierung",
            kpis =
                listOf(
                    Kpi("Diese Woche", "50.75", "h"),
                    Kpi("September", "141.25", "h"),
                    Kpi("Unverrechnet", "CHF 21’046"),
                ),
        ) {
            themedButton("Neue Arbeit", {}, ButtonVariant.TINT)
            themedButton("Start Timer", {}, ButtonVariant.PRIMARY) { icon("play", IconStyle.SOLID) }
        }
        columns(
            side = {
                sideNav(
                    items =
                        listOf(
                            SideItem("Kalender", "/styleguide", "calendar-alt"),
                            SideItem("Meine Stunden", "#stunden", "clock"),
                            SideItem("Offene Tasks", "#tasks", "comment-alt", count = "1"),
                        ),
                    current = "/styleguide",
                )
            }
        ) {
            colours()
            typography()
            controls()
            formControls()
            tableAndCalendar()
            cardVariants()
            feedback()
            formGroups()
            recordContent()
            navigationAndFigures()
        }
    }
}

private const val HEATMAP_STEPS = 6
private const val HEATMAP_FIRST_DAY = 6

private val NAV_ITEMS =
    listOf(
        NavItem("Home", "/organisations"),
        NavItem("Projekte", "#projekte"),
        NavItem("Firmen und Personen", "#firmen"),
        NavItem("Finanzen", "#finanzen"),
        NavItem("Dokumente", "#dokumente"),
    )

/** Closed shapes that have a filled (Solid) variant for active states. */
private val SOLID_ICONS = listOf("star", "play", "pause")

private val SWATCHES =
    listOf(
        "Ground" to GroundVar,
        "Surface" to SurfaceVar,
        "Line" to LineVar,
        "Ink" to InkVar,
        "Ink 3" to Ink3Var,
        "Nav" to NavVar,
        "Primary" to PrimaryVar,
        "Primary tint" to PrimaryTintVar,
        "Hours" to HoursVar,
        "Danger" to DangerVar,
    )

@Composable
private fun colours() {
    card(
        "Farben",
        "Warmes Neutralgrau, ein Blau, Grün nur für Stunden. Dark Mode über den Schalter oben.",
    ) {
        Div(
            attrs = {
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(5, minmax(0, 1fr))")
                    property("gap", "12px")
                }
            }
        ) {
            SWATCHES.forEach { (name, variable) -> swatch(name, variable) }
        }
    }
}

@Composable
private fun swatch(name: String, variable: StyleVariable<*, *>) {
    Div(attrs = StackStyle.toAttrs()) {
        Div(
            attrs = {
                style {
                    property("height", "56px")
                    property("border-radius", "9px")
                    property("border", "1px solid ${LineVar.value()}")
                    property("background", "var(--${variable.name})")
                }
            }
        )
        Span(attrs = SmallStyle.toAttrs()) { Text(name) }
        Span(attrs = listOf(NumStyle, SmallStyle, MutedStyle).toAttrs()) {
            Text("--${variable.name}")
        }
    }
}

@Composable
private fun typography() {
    card(
        "Typografie",
        "Instrument Sans für UI, Dank Mono für Zahlen, Instrument Serif Italic für die Wortmarke.",
    ) {
        H2 { Text("Abschnitt · 20/600") }
        P(attrs = { style { property("margin", "0") } }) { Text("Fliesstext · 14/400") }
        Span(attrs = LabelStyle.toAttrs()) { Text("Label · 12/600 · Versalien") }
        Div(attrs = RowStyle.toAttrs()) {
            Span(attrs = NumStyle.toAttrs { style { property("font-size", "28px") } }) {
                Text("141.25")
            }
            number("CHF 21’046.25")
            Span(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) { Text("Zahlen · Dank Mono") }
        }
        Div(attrs = RowStyle.toAttrs()) {
            Span(attrs = BrandStyle.toAttrs { style { property("font-size", "26px") } }) {
                Text("jessie.ai")
            }
            Span(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                Text("Wortmarke · Serif Italic")
            }
        }
    }
}

@Composable
private fun controls() {
    card("Buttons, Badges, Icons") {
        Div(attrs = RowStyle.toAttrs()) {
            themedButton("Start Timer", {}, ButtonVariant.PRIMARY)
            themedButton("Projekt bearbeiten", {}, ButtonVariant.TINT)
            linkButton("Zurück", "#", ButtonVariant.SECONDARY)
            themedButton("Speichern", {}, ButtonVariant.DARK)
            themedButton("Stornieren", {}, ButtonVariant.DANGER)
            themedButton("Abbrechen", {}, ButtonVariant.GHOST)
            themedButton("Klein", {}, ButtonVariant.SECONDARY, ButtonSize.SMALL)
            themedButton("Deaktiviert", {}, ButtonVariant.PRIMARY, disabled = true)
        }
        Div(attrs = RowStyle.toAttrs()) {
            badge("Verrechenbar", BadgeTone.HOURS)
            badge("Entwurf")
            badge("Offen", BadgeTone.PRIMARY)
            badge("Buchhaltung", BadgeTone.ACCENT)
            badge("Überfällig", BadgeTone.DANGER)
            badge("Coming soon", BadgeTone.WARNING)
        }
        Div(attrs = RowStyle.toAttrs { style { property("gap", "14px") } }) {
            ICONS.keys.forEach { icon(it, IconStyle.REGULAR, label = it) }
            SOLID_ICONS.forEach { icon(it, IconStyle.SOLID, label = "$it (solid)") }
        }
        Div(attrs = TabsStyle.toAttrs()) {
            A(href = "#", attrs = TabStyle.toAttrs { attr("aria-current", "page") }) {
                Text("Projektstruktur")
            }
            A(href = "#", attrs = TabStyle.toAttrs()) { Text("Stunden") }
            A(href = "#", attrs = TabStyle.toAttrs()) { Text("Rechnungen") }
        }
        Div(attrs = SegmentedStyle.toAttrs()) {
            themedSegment("Monat", true)
            themedSegment("Woche", false)
        }
    }
}

@Composable
private fun themedSegment(label: String, pressed: Boolean) {
    Button(
        attrs =
            SegmentStyle.toAttrs {
                attr("type", "button")
                attr("aria-pressed", pressed.toString())
            }
    ) {
        Text(label)
    }
}

@Composable
private fun formControls() {
    var name by remember { mutableStateOf("Sanitas") }
    var rate by remember { mutableStateOf("") }
    var billable by remember { mutableStateOf(true) }
    var active by remember { mutableStateOf(true) }
    card("Formulare", "Label 13/600, optionaler Zusatz in Placeholder-Grau, Fokus mit 3 px Ring.") {
        Div(attrs = Grid2Style.toAttrs()) {
            Label(forId = "sg-name", attrs = FieldStyle.toAttrs()) {
                Text("Name")
                TextInput(
                    name,
                    { name = it },
                    FieldInputStyle.toModifier().id("sg-name"),
                    variant = UnstyledInputVariant,
                )
            }
            Label(forId = "sg-rate", attrs = FieldStyle.toAttrs()) {
                Span {
                    Text("Stundensatz ")
                    Span(attrs = FieldOptionalStyle.toAttrs()) { Text("· optional") }
                }
                TextInput(
                    rate,
                    { rate = it },
                    NumInputStyle.toModifier().id("sg-rate"),
                    variant = UnstyledInputVariant,
                    placeholder = "149.00",
                )
            }
        }
        Label(forId = "sg-desc", attrs = FieldStyle.toAttrs()) {
            Text("Beschreibung")
            TextArea(
                attrs =
                    TextAreaStyle.toAttrs {
                        id("sg-desc")
                        placeholder("Was hast du gemacht?")
                    }
            )
        }
        Div(attrs = RowStyle.toAttrs { style { property("gap", "24px") } }) {
            Label(attrs = RowStyle.toAttrs()) {
                Switch(active, { active = it })
                Text("Aktiv")
            }
            Checkbox(billable, { billable = it }, CheckStyle.toModifier()) {
                Span {
                    Text("Verrechenbar")
                    Span(attrs = CheckHintStyle.toAttrs()) {
                        Text("Nicht verrechenbar z.B. für Offerten, Verkauf oder Reisezeit.")
                    }
                }
            }
        }
    }
}

@Composable
private fun tableAndCalendar() {
    card("Tabelle und Heatmap", "Zahlenspalten rechtsbündig in Dank Mono, Gruppen mit Summe.") {
        Div(
            attrs =
                TableStyle.toAttrs {
                    style {
                        property("border", "1px solid ${LineVar.value()}")
                        property("border-radius", "9px")
                    }
                }
        ) {
            tableRow(HeadTableRowVariant, "Projekt", "Position", "Stunden")
            tableRow(GroupTableRowVariant, "Freitag, 18. September", "", "8.25")
            tableRow(null, "Sanitas", "Projektmitarbeit Stephan 2026", "8.25")
            tableRow(TotalTableRowVariant, "Total September", "", "141.25")
        }
        Div(attrs = RowStyle.toAttrs()) {
            repeat(HEATMAP_STEPS) { step ->
                Span(attrs = DayStyle.toModifier(HeatDayVariants[step]).toAttrs()) {
                    Text("${HEATMAP_FIRST_DAY + step}")
                }
            }
            Span(attrs = DayStyle.toModifier(TodayDayVariant).toAttrs()) { Text("20") }
            Span(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                Text("0h · <4h · 4–6h · 6–8h · 8–10h · >10h · heute")
            }
        }
    }
}

@Composable
private fun tableRow(
    variant: CssStyleVariant<TableRowKind>?,
    first: String,
    second: String,
    hours: String,
) {
    Div(
        attrs =
            TableRowStyle.toModifier(variant).toAttrs {
                style { property("grid-template-columns", "200px minmax(0, 1fr) 100px") }
            }
    ) {
        Span { Text(first) }
        Span { Text(second) }
        Span(attrs = TableNumStyle.toAttrs()) { Text(hours) }
    }
}
